import React from 'react'
import {
  BlockNoteEditor,
  type DefaultBlockSchema,
  type Block,
} from '@packages/blocknote-core'
import { parseTaskState } from './task'
import { reconstructTaskLine } from '../components/shared/BlockRenderer/taskUtils'
import { type TaskItem } from '../modules/filter/types'
import { type Note } from './note'
import { type WindowWithEditor } from './WindowWithEditor'
import { trackError } from '../lib/analytics'

/**
 * Represents a task that was extracted from a drag event
 */
export type DraggedTask = {
  /** The original block ID from the editor */
  blockId: string
  /** The task content in NotePlan format */
  content: string
  /** The parsed task information */
  taskInfo: TaskItem
  /** The source note where the task originated */
  sourceNote?: Note
}

/**
 * Result of extracting tasks from a drag event
 */
export type TaskExtractionResult = {
  /** Successfully extracted tasks */
  tasks: DraggedTask[]
  /** Any errors that occurred during extraction */
  errors: string[]
  /** Whether the extraction was successful */
  success: boolean
}

/**
 * Extracts task content from a drag event's HTML data
 *
 * This function parses the HTML content from a drag event and extracts
 * task information using the existing BlockNoteEditor and task parsing utilities.
 *
 * @param dragEvent - The drag event containing HTML data
 * @param isCloudKitNote - Whether the target is a CloudKit note (affects attachment format)
 * @returns TaskExtractionResult with extracted tasks and any errors
 */
export function extractTasksFromDragEvent(
  dragEvent: DragEvent | React.DragEvent,
  isCloudKitNote: boolean = false
): TaskExtractionResult {

  const result: TaskExtractionResult = {
    tasks: [],
    errors: [],
    success: false,
  }

  try {
    // Get the HTML data from the drag event
    const html = dragEvent.dataTransfer?.getData('text/html')

    if (!html) {
      result.errors.push('No HTML data found in drag event')
      return result
    }

    // Parse the HTML content
    const element = document.createElement('div')
    element.innerHTML = html

    // Extract tasks from each dragged block
    for (let i = 0; i < element.childElementCount; i++) {
      const child = element.children[i]
      const blockId = (child as HTMLElement).getAttribute('data-id')

      if (!blockId) {
        result.errors.push(`Block ${i} missing data-id attribute`)
        continue
      }

      try {
        const draggedTask = extractTaskFromBlock(blockId, isCloudKitNote)
        if (draggedTask) {
          result.tasks.push(draggedTask)
        } else {
          result.errors.push(`Failed to extract task from block ${blockId}`)
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error'
        result.errors.push(`Error extracting block ${blockId}: ${errorMessage}`)
        trackError('taskDragUtils', 'Error extracting task from block', {
          blockId,
          error: errorMessage,
        })
      }
    }

    result.success = result.tasks.length > 0
    return result
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error'
    result.errors.push(
      `Failed to extract tasks from drag event: ${errorMessage}`
    )
    trackError('taskDragUtils', 'Error extracting tasks from drag event', {
      error: errorMessage,
    })
    return result
  }
}

/**
 * Extracts a single task from a block using its ID
 *
 * @param blockId - The block ID to extract
 * @param isCloudKitNote - Whether the target is a CloudKit note
 * @returns DraggedTask if successful, null if not a task or failed
 */
function extractTaskFromBlock(
  blockId: string,
  isCloudKitNote: boolean
): DraggedTask | null {

  // Get the block from the global editor instance
  const editor = (window as WindowWithEditor).editor
  if (!editor) {
    throw new Error('Editor not available on window object')
  }

  const block: Block<DefaultBlockSchema> | undefined = editor.getBlock(blockId)
  if (!block) {
    throw new Error(`Block not found: ${blockId}`)
  }

  // Convert block to NotePlan format - blocksToNotePlan handles children recursively
  const content = BlockNoteEditor.blocksToNotePlan([block], isCloudKitNote)
  if (!content) {
    throw new Error(`Failed to convert block to NotePlan format: ${blockId}`)
  }

  // For drag and drop, we don't need to parse tasks - just preserve the content as-is
  // We'll use a simplified task info for non-task content
  const lines = content.split('\n').filter((line) => line.trim())
  const firstLine = lines[0] || ''
  
  // Try to parse as a task first
  const parsed = parseTaskState(firstLine)
  
  // Create task info - for non-tasks, we'll use default values
  const taskLine = firstLine
  const lineIndex = 0

  // Create a mock source note for now - we'll implement proper source detection later
  const mockSourceNote: Note = {
    recordName: 'drag-source',
    noteType: 0, // CALENDAR_NOTE
    filename: 'drag-source.md',
    parent: null, // eslint-disable-line unicorn/no-null -- required by Note type for Supabase compatibility
    source: 'CLOUDKIT' as any,
    content: content,
    title: 'Drag Source',
    fileModifiedAt: new Date(),
    isEmpty: false,
    isFolder: false,
    tags: [],
  }

  const taskInfo: TaskItem = {
    content: parsed ? parsed.content : firstLine.trim(),
    state: parsed ? parsed.state : '',
    bullet: parsed ? parsed.bullet : '',
    lineIndex,
    sourceNote: mockSourceNote,
    originalLine: taskLine,
  }

  return {
    blockId,
    content: content, // Use the full content including children
    taskInfo,
    sourceNote: mockSourceNote,
  }
}

/**
 * Converts dragged tasks to NotePlan format for insertion into target notes
 *
 * @param draggedTasks - The tasks to convert
 * @returns Array of NotePlan formatted task lines
 */
export function draggedTasksToNotePlan(draggedTasks: DraggedTask[]): string[] {
  return draggedTasks.map((task) => {
    // Use the full content to preserve original formatting and children
    return task.content
  })
}

/**
 * Validates that a drag event contains BlockNote content
 *
 * @param dragEvent - The drag event to validate
 * @returns true if the event contains BlockNote content
 */
export function isDragEventWithTasks(
  dragEvent: DragEvent | React.DragEvent
): boolean {

  const html = dragEvent.dataTransfer?.getData('text/html')

  if (!html) {
    return false
  }

  // Check if it contains any BlockNote content
  const hasBlockNoteContent = html.includes('data-content-type=')
  const result = hasBlockNoteContent


  return result
}

/**
 * Gets the source note information from dragged tasks
 * This is useful for determining if we need to remove tasks from the source
 *
 * @param draggedTasks - The dragged tasks
 * @returns Information about source notes
 */
export function getSourceNoteInfo(draggedTasks: DraggedTask[]): {
  sourceNotes: Set<string>
  needsSourceRemoval: boolean
} {
  const sourceNotes = new Set<string>()

  for (const task of draggedTasks) {
    if (task.sourceNote?.recordName) {
      sourceNotes.add(task.sourceNote.recordName)
    }
  }

  return {
    sourceNotes,
    needsSourceRemoval: sourceNotes.size > 0,
  }
}
